import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { isFunction } from '@fi-shared/utils/is-function';
import { TabsModuleBase } from '@fi-app/utilities/tabs/tabs-module-base';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { AppTextService } from '@fi-shared/app-text';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiUserHelperService } from '@fi-shared/fi-user-shared/fi-user-helper.service';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'fi-core-navigation-tabs',
    templateUrl: './core-navigation-tabs.component.html',
    styleUrls: ['./core-navigation-tabs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-core-navigation-tabs' },
    standalone: false,
})
export class CoreNavigationTabsComponent extends TabsModuleBase implements OnInit {
    FiInfoIconMode = FiInfoIconMode;
    FiStateNames = FiStateNames;
    currentCoreListDisplayType: FiDisplayType;
    currentListDisplayTypeKey: FiItemsListType;
    addSrefDisabled$ = new BehaviorSubject(false);

    constructor(
        injector: Injector,
        private appTextService: AppTextService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
        private userHelperService: FiUserHelperService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private userPreferencesService: UserPreferencesService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.initTabs();
        this.initCurrentListDisplayType();
        this.updateAddSrefDisabled();
    }

    private initTabs(): void {
        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.User.List))
            this.tabs.push({ Title: this.appTextService.get('common.colleagues'), Sref: FiStateNames.User.List });

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.BusinessUnit.List))
            this.tabs.push({ Title: this.appTextService.get('common.units'), Sref: FiStateNames.BusinessUnit.List });

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Area.List))
            this.tabs.push({ Title: this.appTextService.get('common.areas'), Sref: FiStateNames.Area.List });

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Cluster.List))
            this.tabs.push({ Title: this.appTextService.get('common.cluster'), Sref: FiStateNames.Cluster.List });

        if (this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.User.AccessRequests))
            this.tabs.push({ Title: this.appTextService.get('user_requests'), Sref: FiStateNames.User.AccessRequests });

        super.updateSelectedTabFromState(false);
    }

    initCurrentListDisplayType() {
        this.currentListDisplayTypeKey = this.getCoreComponentListDisplayTypeKeyByCurrentState();
        this.currentCoreListDisplayType = this.userPreferencesService.getListDisplayType(
            this.currentListDisplayTypeKey,
        );
        if (typeof this.currentCoreListDisplayType !== 'number') {
            this.updateCurrentListDisplayType(FiDisplayType.List);
        }
    }

    updateCurrentListDisplayType(type: number) {
        this.currentCoreListDisplayType = type;
        this.userPreferencesService.saveListDisplayType(this.currentListDisplayTypeKey, type);
    }

    onTabIndexChange($event: number) {
        super.onTabIndexChange($event);
        setTimeout(() => {
            this.updateAddSrefDisabled();
        });
    }

    private updateAddSrefDisabled() {
        if (this.stateService.current.name !== FiStateNames.User.List) {
            this.addSrefDisabled$.next(false);
            return;
        }

        this.userHelperService.isAddUserEnabled().then((isAddUserEnabled) => {
            this.addSrefDisabled$.next(!isAddUserEnabled);
        });
    }

    getAddSref(): string {
        const data = this.stateService.current.data;
        if (data && data.addSref) {
            const addState = isFunction(data.addSref) ? data.addSref() : data.addSref;
            if (this.stateAccessCheckService.isStateAllowedForCurrentUser(addState)) return addState;
        }
    }

    getCoreComponentListDisplayTypeKeyByCurrentState(): FiItemsListType {
        switch (this.stateService.current.name) {
            case FiStateNames.User.List:
                return FiItemsListType.Colleagues;
            case FiStateNames.BusinessUnit.List:
                return FiItemsListType.Units;
            case FiStateNames.Area.List:
                return FiItemsListType.Areas;
            case FiStateNames.Cluster.List:
                return FiItemsListType.Clusters;
            case FiStateNames.User.AccessRequests:
                return FiItemsListType.UserAccessRequest;
        }
    }

    isCurrentListDisplayTypeList(): boolean {
        return this.currentCoreListDisplayType === FiDisplayType.List;
    }

    getTooltipText(): string {
        if (this.stateService.current.name === FiStateNames.User.List)
            return this.appTextService.get('pk.add_user_disable_tooltip');
    }
}
